/**
 * ResidenceRail — the shared left rail carried by every renovated room.
 *
 * Single source of truth for room order, room tones, and secondary
 * editorial captions ("The Foyer", "The Kitchen", "The Study"...).
 *
 * Navigation labels stay familiar and functional (Family, Meals, Shopping,
 * Money). The room identity appears only as a small tracked caption
 * underneath, per the user's Batch A direction.
 */
import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Masthead, RoomNav, TextLink } from "./editorial";
import { useHomeOSState } from "../../state/HomeOSStateProvider";
import { useAuth } from "../../../../App";

const FALLBACK_HOUSEHOLD = "Your Residence";

/**
 * ROOMS is the OS's canonical directory. Keep in sync with TodayHousePage.
 * Rooms are grouped so every polished room is discoverable — no deep links
 * only. Group headers are quiet Fraunces sub-mastheads, items are the
 * Residence names themselves (The Kitchen, The Study, The Library ...).
 * Each entry ships:
 *   key   — used for aria-current + testid
 *   label — the Residence room name ("The Kitchen", "The Study" ...)
 *   tone  — module tone key from MODULE_TONE
 *   path  — the destination route
 */
export const RESIDENCE_ROOMS = [
  { key: 'today',       label: 'Today',              tone: 'today',     path: '/home-os' },

  // The Family — the people of the household
  { key: 'family',      label: 'The Family Room',    tone: 'family',    path: '/home-os/family-plan', group: 'The Family' },
  { key: 'people',      label: 'People',             tone: 'contacts',  path: '/home-os/contacts',    group: 'The Family' },
  { key: 'mood',        label: 'Mood',               tone: 'family',    path: '/home-os/mood',        group: 'The Family' },

  // The Kitchen — meals & provisions
  { key: 'meals',       label: 'The Kitchen',        tone: 'meals',     path: '/home-os/meals',              group: 'The Kitchen' },
  { key: 'recipes',     label: 'Recipe Book',        tone: 'meals',     path: '/home-os/meals/recipe-book',  group: 'The Kitchen' },
  { key: 'pantry',      label: 'The Pantry',         tone: 'pantry',    path: '/home-os/supplies',           group: 'The Kitchen' },

  // The Study — money, documents & planning
  { key: 'money',       label: 'The Study',          tone: 'money',     path: '/home-os/money',       group: 'The Study' },
  { key: 'library',     label: 'The Library',        tone: 'documents', path: '/home-os/documents',   group: 'The Study' },
  { key: 'planning',    label: 'Planning',           tone: 'goals',     path: '/home-os/calendar',    group: 'The Study' },

  // The Home — the everyday care of the house
  { key: 'wellness',    label: 'The Wellness Room',  tone: 'wellness',  path: '/home-os/health',       group: 'The Home' },
  { key: 'chores',      label: 'Chores',             tone: 'family',    path: '/home-os/chores',       group: 'The Home' },
  { key: 'cleaning',    label: 'Cleaning',           tone: 'cleaning',  path: '/home-os/cleaning',     group: 'The Home' },
  { key: 'maintenance', label: 'Maintenance',        tone: 'home',      path: '/home-os/maintenance',  group: 'The Home' },
  { key: 'keepsake',    label: 'The Keepsake Book',  tone: 'contacts',  path: '/home-os/memories',     group: 'The Home' },
];

export function useResidenceRoom(currentKey) {
  const location = useLocation();
  return useMemo(() => {
    if (currentKey) return currentKey;
    const match = RESIDENCE_ROOMS.find(r => location.pathname === r.path);
    return match?.key || null;
  }, [location.pathname, currentKey]);
}

/**
 * ResidenceRail — full rail composition: masthead + grouped rooms + concierge.
 * Groups are rendered as quiet Fraunces sub-mastheads. The "Today" and
 * "Ask ARA" items live outside groups.
 * Pages just render <ResidenceLayout rail={<ResidenceRail currentKey="meals" />}>.
 */
export function ResidenceRail({ currentKey, residence, dateLabel }) {
  const navigate = useNavigate();
  const resolved = useResidenceRoom(currentKey);
  const { household } = useHomeOSState();
  const { user, logout } = useAuth();
  const residenceName = residence || household?.name || FALLBACK_HOUSEHOLD;

  const today = dateLabel || new Date().toLocaleDateString('en-NZ', {
    weekday: 'long', day: 'numeric', month: 'long',
  });

  // Split rooms into "Today" and grouped rooms
  const todayRoom = RESIDENCE_ROOMS.find(r => r.key === 'today');
  const grouped = RESIDENCE_ROOMS.filter(r => r.key !== 'today').reduce((acc, r) => {
    const g = r.group || 'Other';
    if (!acc[g]) acc[g] = [];
    acc[g].push(r);
    return acc;
  }, {});
  const groupOrder = ['The Family', 'The Kitchen', 'The Study', 'The Home'];

  return (
    <div className="flex flex-col h-full">
      <Masthead residence={residenceName} dateLabel={today} />
      <div className="mt-10 space-y-7">
        {/* Today — always at the top, no group heading */}
        {todayRoom && (
          <RoomNav
            rooms={[todayRoom]}
            currentKey={resolved}
            onNavigate={(key) => {
              const r = RESIDENCE_ROOMS.find(x => x.key === key);
              if (r?.path) navigate(r.path);
            }}
          />
        )}

        {/* Grouped rooms */}
        {groupOrder.map(groupName => {
          const rooms = grouped[groupName];
          if (!rooms?.length) return null;
          return (
            <div key={groupName} data-testid={`rail-group-${groupName.toLowerCase().replace(/\s+/g, '-')}`}>
              <p
                className="text-[10.5px] tracking-[0.22em] uppercase mb-2.5"
                style={{ color: 'var(--ara-ink-soft)', fontWeight: 500 }}
              >
                {groupName}
              </p>
              <RoomNav
                rooms={rooms}
                currentKey={resolved}
                onNavigate={(key) => {
                  const r = RESIDENCE_ROOMS.find(x => x.key === key);
                  if (r?.path) navigate(r.path);
                }}
              />
            </div>
          );
        })}
      </div>

      {/* Ask ARA — the household concierge, positioned as calm end-note */}
      <div className="mt-10 pt-6" style={{ borderTop: '1px solid var(--ara-line-soft)' }}>
        <TextLink testid="rail-ask-ara" onClick={() => navigate('/home-os/command-centre')}>
          Ask ARA
        </TextLink>
        <p className="mt-2 caption-ara" style={{ color: 'var(--ara-ink-soft)', letterSpacing: '0.02em', textTransform: 'none' }}>
          Your household concierge.
        </p>
      </div>

      {/* Resident footer — quiet identity + a way to leave */}
      {user && (
        <div className="mt-8 pt-6" style={{ borderTop: '1px solid var(--ara-line-soft)' }}>
          <p className="caption-ara" style={{ color: 'var(--ara-ink-soft)', letterSpacing: '0.02em', textTransform: 'none' }}>
            Signed in as {user.name || user.email}
          </p>
          <div className="mt-3">
            <TextLink
              testid="rail-sign-out"
              onClick={async () => { await logout(); navigate('/login'); }}
            >
              Sign out
            </TextLink>
          </div>
        </div>
      )}
    </div>
  );
}
